import { computed, Injectable, Signal, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Aplication } from '@domain/types';
import { Observable, skip } from 'rxjs';

export const SELECTED_APLICATION_STORAGE_KEY = 'selectedAplicationRoadId';

export type ImpersonationAplication = {
    roadId: string;
    canRead: boolean;
    canWrite: boolean;
    canViewSessions: boolean;
};

@Injectable({
    providedIn: 'root',
})
export class AplicationService {
    private readonly _aplications = signal<Aplication[]>([]);
    private readonly _selectedRoadId = signal<string | null>(null);

    readonly aplications = this._aplications.asReadonly();

    readonly selectedAplication: Signal<Aplication | null> = computed(() => {
        const roadId = this._selectedRoadId();

        if (roadId === null) {
            return null;
        }

        return this._aplications().find((aplication) => aplication.roadId === roadId) ?? null;
    });

    readonly selectedRoadId: Signal<string | null> = computed(() => this.selectedAplication()?.roadId ?? null);

    /**
     * Emits the new roadId whenever the selected aplication changes. Skips the value
     * that is current at subscription time, so it only fires on actual switches.
     *
     * Shared cancellation primitive: any in-flight-request-sensitive code can do
     * `.pipe(takeUntil(this.aplicationService.aplicationChanged$))` to auto-unsubscribe
     * when the user switches aplication mid-flight, mirroring the `takeUntilDestroyed`
     * idiom already used for component teardown.
     */
    readonly aplicationChanged$: Observable<string | null> = toObservable(this.selectedRoadId).pipe(skip(1));

    readonly hasAdminAplication: Signal<boolean> = computed(() => this._aplications().some((aplication) => aplication.admin));

    readonly selectedAplicationIsAdmin: Signal<boolean> = computed(() => this.selectedAplication()?.admin ?? false);

    readonly selectedAplicationHasImpersonate: Signal<boolean> = computed(() => {
        const aplication = this.selectedAplication();
        return aplication !== null && (aplication.impersonate.read || aplication.impersonate.write || aplication.impersonate.viewSessions);
    });

    readonly selectedAplicationCanViewSessions: Signal<boolean> = computed(() => {
        const aplication = this.selectedAplication();
        return aplication !== null && aplication.impersonate.viewSessions;
    });

    readonly selectedAplicationCanImpersonate: Signal<boolean> = computed(() => {
        const aplication = this.selectedAplication();
        return aplication !== null && (aplication.impersonate.read || aplication.impersonate.write);
    });

    readonly impersonationAplications: Signal<ImpersonationAplication[]> = computed(() =>
        this._aplications()
            .filter((aplication) => aplication.impersonate.read || aplication.impersonate.write || aplication.impersonate.viewSessions)
            .map((aplication) => ({
                roadId: aplication.roadId,
                canRead: aplication.impersonate.read,
                canWrite: aplication.impersonate.write,
                canViewSessions: aplication.impersonate.viewSessions,
            }))
    );

    /**
     * Replaces the known aplications and (re)computes the active selection: restore the
     * persisted roadId if it is still among the given aplications, otherwise select the
     * first one. Called once per login with the freshly parsed roles.
     */
    setAplications(aplications: Aplication[]): void {
        this._aplications.set(aplications);

        const persistedRoadId = this.readPersistedRoadId();
        const isPersistedValid = persistedRoadId !== null && aplications.some((aplication) => aplication.roadId === persistedRoadId);
        const nextRoadId = isPersistedValid ? persistedRoadId : (aplications[0]?.roadId ?? null);

        this._selectedRoadId.set(nextRoadId);
        this.persistRoadId(nextRoadId);
    }

    /**
     * Switches the active aplication. No-op when the roadId is not among the known aplications.
     */
    selectAplication(roadId: string): void {
        const exists = this._aplications().some((aplication) => aplication.roadId === roadId);

        if (!exists) {
            return;
        }

        this._selectedRoadId.set(roadId);
        this.persistRoadId(roadId);
    }

    private readPersistedRoadId(): string | null {
        try {
            return localStorage.getItem(SELECTED_APLICATION_STORAGE_KEY);
        } catch {
            return null;
        }
    }

    private persistRoadId(roadId: string | null): void {
        try {
            if (roadId === null) {
                localStorage.removeItem(SELECTED_APLICATION_STORAGE_KEY);
                return;
            }

            localStorage.setItem(SELECTED_APLICATION_STORAGE_KEY, roadId);
        } catch {
            // localStorage unavailable (e.g. private browsing) — selection stays in-memory only
        }
    }
}
